import {ThreadHeader} from 'shells/inbox/thread-header/thread-header';
import type {Recipient, UpdateAction} from 'shells/inbox/types';
import {UserSelectButton} from 'shells/user-select-button/user-select-button';

import type {InboxThreadFragment, ThreadType} from 'nutshell-graphql-types';

import {useGetUsers} from '../../core/select/hooks';
import {useGetSessionUser} from '../../core/use-get-session-user';

import {
    useGetInboxThreadById,
    useReassignThread,
    useUpdateThreadStatus,
    useUpdateContactName,
} from '../graphql/hooks';
import {getCanThreadBeReassigned, getThreadStatusFromEffectiveStatus} from '../helpers';

const UNASSIGNED_USER_ID = 'unassigned';
const UNASSIGNED_USER = {
    __typename: 'User',
    id: UNASSIGNED_USER_ID,
    name: 'Unassigned',
    initials: '',
    avatarUrl: '',
    isAgent: false,
    isEnabled: true,
    calendarSync: null,
} as const;

interface Props {
    thread: InboxThreadFragment | null | undefined;
    recipient: Recipient | null | undefined;
    threadType: ThreadType | null | undefined;
    refetchThreads?: () => any;
    fetchMostRecentChannelEvents: () => Promise<any>;
    isLoadingChannelEvents: boolean;
}

export function InboxThreadHeader(props: Props) {
    const {thread, recipient} = props;

    const {user} = useGetSessionUser();
    const {users} = useGetUsers();
    const {reassignThread} = useReassignThread();
    const {updateThreadStatus} = useUpdateThreadStatus();
    const {refetch: refetchThread} = useGetInboxThreadById(thread ? thread.id : null);
    const {updateContactName} = useUpdateContactName();

    const ownerId =
        thread && thread.owner && thread.owner.id && typeof thread.owner.id === 'string'
            ? thread.owner.id
            : null;

    const filteredAgents = users.filter((userModel) => userModel.isAgent);
    let agents = filteredAgents;
    if (user && user.id) {
        const currentUser = filteredAgents.find((agent) => agent.id === user.id);
        if (currentUser) {
            agents = [currentUser, ...filteredAgents.filter((agent) => agent.id !== user.id)];
        }
    }

    if (!thread || !recipient) {
        return null;
    }

    if (ownerId) {
        agents.unshift(UNASSIGNED_USER);
    }

    const shouldShowStatusDot = Boolean(
        thread.externalChannel.type === 'TYPE_CHAT_EXTERNAL' && thread.isOpen
    );

    return (
        <ThreadHeader
            recipient={props.recipient}
            canArchive={thread.canArchive}
            canReopen={thread.canReopen}
            isLoadingChannelEvents={props.isLoadingChannelEvents}
            shouldShowStatusDot={shouldShowStatusDot}
            isSmall={true}
            onUpdateStatus={(status: UpdateAction) => {
                return updateThreadStatus(thread.id, status).then(() => {
                    refetchThread();
                    props.refetchThreads?.();
                    props.fetchMostRecentChannelEvents();
                });
            }}
            onSaveContactName={(name: string) => {
                updateContactName({
                    contact: {
                        id: recipient.id,
                        name: name,
                    },
                }).then(() => {
                    props.refetchThreads?.();
                });
            }}
            status={getThreadStatusFromEffectiveStatus(thread.effectiveStatus)}
            assigneeButton={
                <UserSelectButton
                    selectedUserId={ownerId}
                    onSelect={(assigneeId?: string | null) => {
                        const selectedUser = agents.find(
                            (userModel) => userModel.id === assigneeId
                        );

                        if (selectedUser) {
                            reassignThread({
                                threadId: thread.id,
                                assigneeId:
                                    selectedUser.id === UNASSIGNED_USER_ID ? null : selectedUser.id,
                                assigneeName: selectedUser.name,
                                assigneeAvatarUrl: selectedUser.avatarUrl,
                                assigneeInitials: selectedUser.initials,
                            }).then(() => {
                                refetchThread();
                                props.refetchThreads?.();
                                props.fetchMostRecentChannelEvents();
                            });
                        }
                    }}
                    users={agents}
                    sortInitialValueToTop={false}
                    isReadOnly={!getCanThreadBeReassigned(thread)}
                />
            }
            threadType={props.threadType}
        />
    );
}
